import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import { RegistryBar, RegistryEmpty, RegistryToolbar } from '@shared/ui/RegistryShell';
import { useAppSelector } from '@app/hooks';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { lawsuitStatusLabel, lawsuitStatusTone } from '@shared/lib/statusLabels';
import {
  useListLawsuitsQuery,
  useDeleteLawsuitMutation,
  useGetLawsuitQuery,
} from '@shared/api';
import { CreateLawsuitModal } from '@widgets/CreateLawsuitModal';
import { lawsuitClaimLabel, lawsuitContractLabel } from '@entities/legal/types';
import { useAgentPermission } from '@entities/user';

/**
 * Вкладка «Иски» внутри страницы /agents/statut/claims (см. Claims.tsx).
 * Реестр исков (Lawsuit), параллельный реестру претензий — отдельный пайплайн
 * ADR-0001 для генерации искового заявления через RAG + LLM + doc-processor.
 *
 * Скачивание готового DOCX: при status='ready' эндпоинт getLawsuit вернёт
 * composed[0].id — на нём вызываем downloadAuthorizedFile к тому же
 * `/composed/{id}/download`, что для ответов на претензии и протоколов.
 * Для оптимизации в таблице мы не дёргаем getLawsuit на каждую строку —
 * вместо этого выносим ленивую загрузку DOCX в подкомпонент DownloadCell,
 * который активируется только если status='ready' (см. ниже).
 */

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.55;
`;

const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const TitleLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

const RefLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.radius.xs};
  }
`;

const ManualHint = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const ActionsCell = styled.td`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  justify-content: flex-end;
`;

const ErrorHint = styled.span`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 12px;
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

interface DownloadButtonProps {
  lawsuitId: string;
  title: string;
}

/**
 * Подкомпонент для кнопки «Скачать DOCX». Запрос getLawsuit делается только
 * при монтировании этой ячейки — то есть для строк со status='ready'.
 * Это позволяет получить composed[0].id без отдельного back-эндпоинта.
 */
function DownloadButton({ lawsuitId, title }: DownloadButtonProps) {
  const { data, isLoading } = useGetLawsuitQuery(lawsuitId);
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [downloading, setDownloading] = useState(false);

  const composedId = data?.composed?.[0]?.id ?? null;

  async function onClick() {
    if (!composedId) return;
    setDownloading(true);
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
      const res = await downloadAuthorizedFile(
        `${base}/agents/statut/composed/${composedId}/download`,
        { accessToken, fallbackName: `${title || 'Иск'}.docx` },
      );
      if (!res.ok) {
        window.alert(`Не удалось скачать DOCX (${res.error ?? `HTTP ${res.status}`}).`);
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Button
      type="button"
      $variant="secondary"
      $size="sm"
      onClick={onClick}
      disabled={isLoading || !composedId || downloading}
      title={
        composedId
          ? 'Скачать готовое исковое заявление в DOCX'
          : 'Файл ещё не готов'
      }
    >
      {downloading ? 'Готовим…' : 'Скачать DOCX'}
    </Button>
  );
}

export function LawsuitsTab() {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const { data, isLoading } = useListLawsuitsQuery();
  const [deleteLawsuit] = useDeleteLawsuitMutation();
  const [createOpen, setCreateOpen] = useState(false);

  const items = data?.items ?? [];

  async function onDelete(id: string, title: string) {
    if (!window.confirm(`Удалить иск «${title}»? Действие необратимо.`)) {
      return;
    }
    try {
      await deleteLawsuit(id).unwrap();
    } catch (err) {
      const detail =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        'Не удалось удалить иск. Попробуйте ещё раз.';
      window.alert(detail);
    }
  }

  return (
    <>
      <Note>
        Реестр исковых заявлений. Создайте иск из существующей претензии или
        загрузите файл претензии вручную — агент составит проект искового
        заявления по корпоративному шаблону, со ссылками на ГК и АПК.
      </Note>

      <RegistryToolbar>
        <div />
        <Button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Создать иск
        </Button>
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${items.length}`}
        </span>
      </RegistryBar>

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Название</th>
              <th>Статус</th>
              <th>Исходная претензия</th>
              <th>Договор</th>
              <th>Создан</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width="70%" $height={16} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="60%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width="60%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={140} $height={28} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={6}>
                  Пока нет исков. Создайте первый из существующей претензии
                  или загрузите файлы вручную.
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((l) => (
                <tr key={l.id}>
                  <td>
                    <TitleCell>
                      <TitleLink to={`/agents/statut/lawsuits/${l.id}`}>
                        {l.title}
                      </TitleLink>
                      {l.status === 'failed' && l.error_message && (
                        <ErrorHint title={l.error_message}>
                          {l.error_message.length > 80
                            ? `${l.error_message.slice(0, 80)}…`
                            : l.error_message}
                        </ErrorHint>
                      )}
                    </TitleCell>
                  </td>
                  <td>
                    <Badge $tone={lawsuitStatusTone(l.status)}>
                      {lawsuitStatusLabel(l.status)}
                    </Badge>
                  </td>
                  <td>
                    {l.claim_id ? (
                      <RefLink
                        to={`/agents/statut/claims/${l.claim_id}`}
                        title={lawsuitClaimLabel(l)}
                      >
                        {lawsuitClaimLabel(l)}
                      </RefLink>
                    ) : l.manual_claim_filename ? (
                      <ManualHint title={l.manual_claim_filename}>
                        Файл: {l.manual_claim_filename}
                      </ManualHint>
                    ) : (
                      <ManualHint>—</ManualHint>
                    )}
                  </td>
                  <td>
                    {l.contract_id ? (
                      <RefLink
                        to={`/agents/statut/contracts/${l.contract_id}`}
                        title={lawsuitContractLabel(l)}
                      >
                        {lawsuitContractLabel(l)}
                      </RefLink>
                    ) : l.manual_contract_filename ? (
                      <ManualHint title={l.manual_contract_filename}>
                        Файл: {l.manual_contract_filename}
                      </ManualHint>
                    ) : (
                      <ManualHint>—</ManualHint>
                    )}
                  </td>
                  <td>{formatDate(l.created_at)}</td>
                  <ActionsCell>
                    <Button
                      type="button"
                      $variant="secondary"
                      $size="sm"
                      onClick={() => navigate(`/agents/statut/lawsuits/${l.id}`)}
                    >
                      Открыть
                    </Button>
                    {l.status === 'ready' && (
                      <DownloadButton lawsuitId={l.id} title={l.title} />
                    )}
                    <Button
                      type="button"
                      $variant="secondary"
                      $size="sm"
                      onClick={() => onDelete(l.id, l.title)}
                      disabled={!can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                    >
                      Удалить
                    </Button>
                  </ActionsCell>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <CreateLawsuitModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          // После создания + постановки в очередь — открываем карточку иска,
          // юрист увидит спиннер «Составляется…» и дождётся WS-события.
          navigate(`/agents/statut/lawsuits/${id}`);
        }}
      />
    </>
  );
}
